/** @prettier */

import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {
  Box,
  Container,
  Grid,
  Switch,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import dayjs, {Dayjs} from 'dayjs';
import {useLoaderData, useParams, useNavigate} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

//MTH component import
import TextFieldSelectBox from '../../component/SelectBox';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//interface import
import * as apiIF from '../../Type/api';
import {qtonHydrolutionPROExistence} from './PeriodicReport';
//API import
import {
  getCompositionsGateway,
  getCompositionsRTUnit,
  getCompositionsSitesBySiteID,
  getSettingUser,
  patchCompositionsRTUnit,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

import {pullDownMenu, format, limit} from '../../define';
import {decideDayjsDatePickerLocale} from '../../functions/decideDayjsDatePickerLocale';

type LoaderReturnType = {
  /** ユニットの登録状況 */
  siteStates: qtonHydrolutionPROExistence;
  /** サイト内の全RT情報 */
  compositionsInSite: apiIF.res.getCompositionsRTUnit[];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }
  try {
    //サイトの情報を取得
    const siteInfo = await getCompositionsSitesBySiteID(params.siteID);
    if (siteInfo['site-authority'] !== 'admin') {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    //サイト内のゲートウェイ情報を取得する。
    const gwInfo = await getCompositionsGateway(params.siteID, {
      'fields': 'register-number',
      'max-result': limit.REGISTRATION_NUM_GW_MAX,
    });

    /** Q-tonとHydrolution PROの登録状況 */
    const result: qtonHydrolutionPROExistence = {
      hasQton: false,
      hasHydrolutionPRO: false,
    }; //初期値

    const getRtUnitPromises = [];
    //Q-tonとHydrolution PROの登録状況の更新と定期レポート関連の情報を取得を行う。
    for (const info of gwInfo['gateway-informations']) {
      //熱源機登録状況の更新
      if (info['register-number'] === undefined) continue;
      if (Number(info['register-number']['qton']) > 0) {
        result.hasQton = true;
      }
      if (Number(info['register-number']['chiller']) > 0) {
        result.hasHydrolutionPRO = true;
      }
      //Q-tonもHydrolution PROも登録されていないゲートウェイの場合、情報は取得しない。
      if (
        Number(info['register-number']['qton']) < 0 &&
        Number(info['register-number']['chiller']) < 0
      ) {
        continue;
      }
      //定期レポート関連の情報を取得
      getRtUnitPromises.push(
        getCompositionsRTUnit(params.siteID, info['gateway-id'], {
          'fields':
            'unit-name,unit-type,nameplate,input-voltage,periodic-report',
          'max-result': 400,
        })
      );
    }

    const getRtUnitResponse = await Promise.all(getRtUnitPromises);

    return {
      siteStates: result,
      compositionsInSite: getRtUnitResponse,
    };
  } catch {
    return undefined;
  }
}

/**
 * 定期レポート設定画面コンポーネント
 * @returns JSX.Element
 */
const PeriodicReportSetting = () => {
  const trans = useTranslation().t; //翻訳関数

  //パスパラメータ取得
  const {siteID} = useParams();
  //ローダーデータ取得
  const loaderData = useLoaderData() as LoaderReturnType | undefined;

  //prettier-ignore
  const [compositionsInSite, setCompositionsInSite] = useState<LoaderReturnType['compositionsInSite']>([]); //GW毎の熱源機の情報
  //prettier-ignore
  const [menuItemOfUnitTypes, setMenuItemOfUnitTypes] = useState<{value: 'qton' | 'hydrolutionPRO'; label: string;}[]>([]); //ユニットタイプのメニューリスト
  //prettier-ignore
  const [selectedUnitType, setSelectedUnitType] = useState<'qton' | 'hydrolutionPRO' | ''>(''); //選択されたユニットタイプ
  //prettier-ignore
  const [menuItemOfUnitName, setMenuItemOfUnitName] = useState<{value: string; label: string}[]>([]); //ユニット名のメニューリスト
  //prettier-ignore
  const [selectedUnitID, setSelectedUnitID] = useState<apiIF.resParameters.rtUnitInformations['unit-id']>(''); //選択されたユニットのID
  //prettier-ignore
  const [periodicReportInfo, setPeriodicReportInfo] = useState<apiIF.resParameters.rtUnitInformations['periodic-report']>(undefined); //定期レポート情報
  //prettier-ignore
  const [reportGeneration, setReportGeneration] = useState<apiIF.reqParameters.periodicReportSetting['report-generation']>('inactive'); //レポート生成有無
  const [gwID, setGwID] = useState(''); //ゲートウェイID
  const [unitID, setUnitID] = useState(''); //ユニットID
  //prettier-ignore
  const [nameplate, setNameplate] = useState<apiIF.resParameters.rtUnitInformations['nameplate']>(''); //銘鈑
  //prettier-ignore
  const [inputVoltage, setInputVoltage] = useState<apiIF.resParameters.rtUnitInformations['input-voltage']>(''); //入力電圧

  //画面遷移時
  useEffect(() => {
    if (loaderData === undefined) {
      return;
    }
    //GW毎の熱源機の情報
    setCompositionsInSite(loaderData.compositionsInSite);
    //ユニットタイプのメニューアイテム作成
    const hasQton = loaderData.siteStates.hasQton;
    const hasHydrolutionPRO = loaderData.siteStates.hasHydrolutionPRO;
    if (hasQton === true && hasHydrolutionPRO === true) {
      setMenuItemOfUnitTypes([
        {value: 'qton', label: 'Q-ton'},
        {value: 'hydrolutionPRO', label: 'Hydrolution PRO'},
      ]);
      setSelectedUnitType('qton');
    } else if (hasQton === true && hasHydrolutionPRO === false) {
      setMenuItemOfUnitTypes([{value: 'qton', label: 'Q-ton'}]);
      setSelectedUnitType('qton');
    } else if (hasQton === false && hasHydrolutionPRO === true) {
      setMenuItemOfUnitTypes([
        {value: 'hydrolutionPRO', label: 'Hydrolution PRO'},
      ]);
      setSelectedUnitType('hydrolutionPRO');
    }
  }, []);

  //ユニットタイプ選択時
  useEffect(() => {
    //ユニットIDを初期化
    setSelectedUnitID('');

    // ユニット名のメニューリスト作成
    const menuItemOfQtonName = [];
    const menuItemOfChillerName = [];
    for (const compositionsRTUnit of compositionsInSite) {
      for (const unitInfo of compositionsRTUnit['unit-informations']) {
        if (unitInfo['unit-name'] === undefined) {
          continue;
        }
        if (
          unitInfo['unit-type'] === 'QtonOpen' ||
          unitInfo['unit-type'] === 'QtonClose'
        ) {
          menuItemOfQtonName.push({
            value: unitInfo['unit-id'],
            label: unitInfo['unit-name'],
          });
        } else if (unitInfo['unit-type'] === 'Chiller') {
          menuItemOfChillerName.push({
            value: unitInfo['unit-id'],
            label: unitInfo['unit-name'],
          });
        }
      }
    }
    if (selectedUnitType === 'qton') {
      setMenuItemOfUnitName(menuItemOfQtonName);
    } else if (selectedUnitType === 'hydrolutionPRO') {
      setMenuItemOfUnitName(menuItemOfChillerName);
    }
  }, [selectedUnitType]);

  //ユニット名選択時
  useEffect(() => {
    for (const compositionsRTUnit of compositionsInSite) {
      for (const unitInfo of compositionsRTUnit['unit-informations']) {
        if (unitInfo['unit-id'] !== selectedUnitID) continue;
        //定期レポート情報がない場合は終了
        if (unitInfo['periodic-report'] === undefined) {
          break;
        }

        //定期レポート情報設定
        setPeriodicReportInfo(unitInfo['periodic-report']);
        setReportGeneration(unitInfo['periodic-report']['report-generation']);

        //銘鈑と入力電圧設定
        setNameplate(unitInfo['nameplate']);
        setInputVoltage(unitInfo['input-voltage']);

        //API呼び出しに必要なためゲートウェイIDとユニットID設定
        const validateID = unitInfo['unit-id'].split('+'); //[0]：ユニットの種類、[1]：ゲートウェイID、[2]：rtID
        setGwID(validateID[1]);
        setUnitID(unitInfo['unit-id']);
      }
    }
  }, [selectedUnitID]);

  /** トグルボタン押下時処理 */
  const switchHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked === true) {
      setReportGeneration('active');
    } else {
      setReportGeneration('inactive');
    }
  };

  if (siteID === undefined) return <></>;
  return (
    <Container fixed className="serviceInfo__periodicReportSetting">
      <Grid container spacing={4} justifyContent={'center'}>
        <Grid item xs={12}>
          <Box>
            <h1>{trans('PeriodicReportSetting.title')}</h1>
          </Box>
          <Box className="serviceInfo__periodicReportSetting__text--required">
            {trans('PeriodicReportSetting.note')}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Stack
            className="serviceInfo__periodicReportSetting__contents__unitSelect"
            spacing={5}>
            {/** ユニットタイプ */}
            <TextFieldSelectBox
              choices={menuItemOfUnitTypes}
              setValue={setSelectedUnitType}
              value={selectedUnitType}
              id="unitTypeSelect"
              label={`${trans('PeriodicReportSetting.unitType')}*`}
              class="serviceInfo__periodicReportSetting__contents__unitSelect__textFieldSelectBox"
            />
            {/** ユニット名 */}
            {selectedUnitType === '' ? (
              <></>
            ) : (
              <TextFieldSelectBox
                choices={menuItemOfUnitName}
                setValue={setSelectedUnitID}
                id="unitNameSelect"
                label={`${trans('PeriodicReportSetting.unitName')}*`}
                value={selectedUnitID}
                class="serviceInfo__periodicReportSetting__contents__unitSelect__textFieldSelectBox"
              />
            )}
            {/** レポート生成有無 */}
            {selectedUnitID === '' ? (
              <></>
            ) : (
              <Grid container alignItems="center">
                <Grid
                  item
                  xs="auto"
                  className="serviceInfo__periodicReportSetting__contents__reportGeneration">
                  {`${trans('PeriodicReportSetting.reportGeneration')}*`}
                </Grid>
                <Grid item>
                  <Switch
                    checked={reportGeneration === 'active'}
                    onChange={switchHandleChange}
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        </Grid>
        {selectedUnitType === '' ||
        selectedUnitID === '' ||
        periodicReportInfo === undefined ? (
          <></>
        ) : selectedUnitType === 'qton' ? (
          /* Q-tonレポート */
          <QtonReportSetting
            siteID={siteID}
            gwID={gwID}
            unitID={unitID}
            nameplate={nameplate}
            inputVoltage={inputVoltage}
            reportGeneration={reportGeneration}
            periodicReportInfo={periodicReportInfo}
            setCompositionsInSite={setCompositionsInSite}
            trans={trans}
          />
        ) : selectedUnitType === 'hydrolutionPRO' ? (
          /* Hydrolution PROレポート */
          <ChillerReportSetting
            siteID={siteID}
            gwID={gwID}
            unitID={unitID}
            nameplate={nameplate}
            inputVoltage={inputVoltage}
            reportGeneration={reportGeneration}
            periodicReportInfo={periodicReportInfo}
            setCompositionsInSite={setCompositionsInSite}
            trans={trans}
          />
        ) : (
          <></>
        )}
      </Grid>
    </Container>
  );
};

/**
 * Q-tonの定期レポート設定画面コンポーネント
 * @param props props
 * @returns JSX.Element
 * */
const QtonReportSetting = (props: {
  /** サイトID */
  siteID: string;
  /** ゲートウェイID */
  gwID: string;
  /** ユニットID */
  unitID: string;
  /** 銘鈑 */
  nameplate: apiIF.resParameters.rtUnitInformations['nameplate'];
  /** 入力電圧 */
  inputVoltage: apiIF.resParameters.rtUnitInformations['input-voltage'];
  /** レポート生成有無 */
  reportGeneration: apiIF.reqParameters.periodicReportSetting['report-generation'];
  /** 選択したユニットの定期レポート情報 */
  periodicReportInfo: apiIF.resParameters.periodicReportSetting;
  //prettier-ignore
  /** GW毎の熱源機の情報（set関数） */
  setCompositionsInSite: React.Dispatch<React.SetStateAction<apiIF.res.getCompositionsRTUnit[]>>;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  const navigate = useNavigate();

  //prettier-ignore
  const [serialNumber, setSerialNumber] = useState<apiIF.reqParameters.periodicReportSetting['serial-number']>(props.periodicReportInfo['serial-number']); //シリアル番号
  //prettier-ignore
  const [installationDate, setInstallationDate] = useState<Dayjs | null>(dayjs(props.periodicReportInfo['install-date'])); //納入日
  //prettier-ignore
  const [co2EmissionFactor, setCo2EmissionFactor] = useState<apiIF.reqParameters.periodicReportSetting['co2-emission-factor'] >(props.periodicReportInfo['co2-emission-factor']); //CO2排出係数
  const [reportIssueMonth, setReportIssueMonth] = useState(() => {
    if (props.periodicReportInfo['report-issue'].includes('1')) {
      return '0';
    } else if (props.periodicReportInfo['report-issue'].includes('2')) {
      return '1';
    } else {
      return '2';
    }
  }); //レポート発行月
  //prettier-ignore
  const [currency, setCurrency] = useState<apiIF.reqParameters.periodicReportSetting['currency']>(props.periodicReportInfo['currency']); //通貨単位
  //prettier-ignore
  const [priceA, setPriceA] = useState<apiIF.reqParameters.periodicReportSetting['priceA']>(props.periodicReportInfo['priceA']); //電気代A
  //prettier-ignore
  const [priceARate, setPriceARate] = useState<apiIF.reqParameters.periodicReportSetting['priceA-rate']>(props.periodicReportInfo['priceA-rate']); //電気代A単価
  //prettier-ignore
  const [priceB, setPriceB] = useState<apiIF.reqParameters.periodicReportSetting['priceB']>(props.periodicReportInfo['priceB']); //電気代B
  //prettier-ignore
  const [priceBRate, setPriceBRate] = useState<apiIF.reqParameters.periodicReportSetting['priceB-rate']>(props.periodicReportInfo['priceB-rate']); //電気代B単価
  //prettier-ignore
  const [priceC, setPriceC] = useState<apiIF.reqParameters.periodicReportSetting['priceC']>(props.periodicReportInfo['priceC']); //電気代C
  //prettier-ignore
  const [priceCRate, setPriceCRate] = useState<apiIF.reqParameters.periodicReportSetting['priceC-rate']>(props.periodicReportInfo['priceC-rate']); //電気代C単価
  //prettier-ignore
  const [priceD, setPriceD] = useState<apiIF.reqParameters.periodicReportSetting['priceD']>(props.periodicReportInfo['priceD']); //電気代D
  //prettier-ignore
  const [priceDRate, setPriceDRate] = useState<apiIF.reqParameters.periodicReportSetting['priceD-rate']>(props.periodicReportInfo['priceD-rate']); //電気代D単価
  //prettier-ignore
  const [priceE, setPriceE] = useState<apiIF.reqParameters.periodicReportSetting['priceE']>(props.periodicReportInfo['priceE']); //電気代E
  //prettier-ignore
  const [priceERate, setPriceERate] = useState<apiIF.reqParameters.periodicReportSetting['priceE-rate']>(props.periodicReportInfo['priceE-rate']); //電気代E単価
  //prettier-ignore
  const [priceF, setPriceF] = useState<apiIF.reqParameters.periodicReportSetting['priceF']>(props.periodicReportInfo['priceF']); //電気代F
  //prettier-ignore
  const [priceFRate, setPriceFRate] = useState<apiIF.reqParameters.periodicReportSetting['priceF-rate']>(props.periodicReportInfo['priceF-rate']); //電気代F単価
  //prettier-ignore
  const [priceSchedule, setPriceSchedule] = useState<apiIF.reqParameters.periodicReportSetting['price-schedule']>(props.periodicReportInfo['price-schedule']); //電気料金スケジュール
  //prettier-ignore
  const [alternativeFuel1, setAlternativeFuel1] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel1']>(props.periodicReportInfo['alternative-fuel1']); //代替燃料1
  //prettier-ignore
  const [alternativeFuel1Rate, setAlternativeFuel1Rate] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel1-rate']>(props.periodicReportInfo['alternative-fuel1-rate']); //代替燃料1単価
  //prettier-ignore
  const [alternativeFuel1Unit, setAlternativeFuel1Unit] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel1-unit']>(props.periodicReportInfo['alternative-fuel1-unit']); //代替燃料1単位
  //prettier-ignore
  const [alternativeFuel1Efficiency, setAlternativeFuel1Efficiency] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel1-efficiency']>(props.periodicReportInfo['alternative-fuel1-efficiency']); //代替燃料1システム効率
  //prettier-ignore
  const [alternativeFuel1Co2, setAlternativeFuel1Co2] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel1-co2-emission-factor']>(props.periodicReportInfo['alternative-fuel1-co2-emission-factor']); //代替燃料1用CO2排出係数
  //prettier-ignore
  const [alternativeFuel2, setAlternativeFuel2] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel2']>(props.periodicReportInfo['alternative-fuel2']); //代替燃料2
  //prettier-ignore
  const [alternativeFuel2Rate, setAlternativeFuel2Rate] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel2-rate']>(props.periodicReportInfo['alternative-fuel2-rate']); //代替燃料2単価
  //prettier-ignore
  const [alternativeFuel2Unit, setAlternativeFuel2Unit] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel2-unit']>(props.periodicReportInfo['alternative-fuel2-unit']); //代替燃料2単位
  //prettier-ignore
  const [alternativeFuel2Efficiency, setAlternativeFuel2Efficiency] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel2-efficiency']>(props.periodicReportInfo['alternative-fuel2-efficiency']); //代替燃料2システム効率
  //prettier-ignore
  const [alternativeFuel2Co2, setAlternativeFuel2Co2] = useState<apiIF.reqParameters.periodicReportSetting['alternative-fuel2-co2-emission-factor']>(props.periodicReportInfo['alternative-fuel2-co2-emission-factor']); //代替燃料2用CO2排出係数
  //prettier-ignore
  const [serialNumberError, setSerialNumberError] = useState(false); //製造番号
  //prettier-ignore
  const [co2EmissionFactorError, setCo2EmissionFactorError] = useState(false); //CO2排出係数
  //prettier-ignore
  const [pricePlanError, setPricePlanError] = useState(false); //電気料金プラン入力欄のエラー有無
  //prettier-ignore
  const [pricePlanRateError, setPricePlanRateError] = useState(false); //電気料金プラン単価のエラー有無
  //prettier-ignore
  const [priceScheduleError, setPriceScheduleError] = useState(false); //電気料金スケジュールのエラー有無
  //prettier-ignore
  const [alternativeFuel1Error, setAlternativeFuel1Error] = useState(false); //代替燃料1エラー有無
  //prettier-ignore
  const [alternativeFuel1RateError, setAlternativeFuel1RateError] = useState(false); //代替燃料1単価エラー有無
  //prettier-ignore
  const [alternativeFuel1EfficiencyError, setAlternativeFuel1EfficiencyError] = useState(false); //代替燃料1システム効率エラー有無
  //prettier-ignore
  const [alternativeFuel1Co2Error, setAlternativeFuel1Co2Error] = useState(false); //代替燃料1CO2排出係数エラー有無
  //prettier-ignore
  const [alternativeFuel2Error, setAlternativeFuel2Error] = useState(false); //代替燃料2エラー有無
  //prettier-ignore
  const [alternativeFuel2RateError, setAlternativeFuel2RateError] = useState(false); //代替燃料1単価エラー有無
  //prettier-ignore
  const [alternativeFuel2EfficiencyError, setAlternativeFuel2EfficiencyError] = useState(false); //代替燃料1システム効率エラー有無
  //prettier-ignore
  const [alternativeFuel2Co2Error, setAlternativeFuel2Co2Error] = useState(false); //代替燃料1CO2排出係数エラー有無
  //prettier-ignore
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english'); //言語情報
  //API関連
  //prettier-ignore
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  //prettier-ignore
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  //prettier-ignore
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState(false); //API成功時メッセージ表示

  /**
   * レポート発行月の変換テーブル
   * selectboxのvalueに配列を設定することが難しいため、
   * テーブルを用意し、API送信時に変換をする。
   * 別keyで同一valueがあるのは機種変更時に画面更新をするため。
   */
  const reportIssueConvertTable: {
    [key: string]: apiIF.reqParameters.periodicReportSetting['report-issue'];
  } = {
    '0': ['1', '4', '7', '10'],
    '1': ['2', '5', '8', '11'],
    '2': ['3', '6', '9', '12'],
  };

  useEffect(() => {
    setSerialNumber(props.periodicReportInfo['serial-number']);
    setInstallationDate(
      dayjs(
        props.periodicReportInfo['install-date'] === ''
          ? undefined
          : props.periodicReportInfo['install-date']
      )
    );
    setCo2EmissionFactor(props.periodicReportInfo['co2-emission-factor']);
    //レポート発行月の変換値を設定
    if (props.periodicReportInfo['report-issue'].includes('1')) {
      setReportIssueMonth('0');
    } else if (props.periodicReportInfo['report-issue'].includes('2')) {
      setReportIssueMonth('1');
    } else if (props.periodicReportInfo['report-issue'].includes('3')) {
      setReportIssueMonth('2');
    }
    setCurrency(props.periodicReportInfo['currency']);
    setPriceA(props.periodicReportInfo['priceA']);
    setPriceARate(props.periodicReportInfo['priceA-rate']);
    setPriceB(props.periodicReportInfo['priceB']);
    setPriceBRate(props.periodicReportInfo['priceB-rate']);
    setPriceC(props.periodicReportInfo['priceC']);
    setPriceCRate(props.periodicReportInfo['priceC-rate']);
    setPriceD(props.periodicReportInfo['priceD']);
    setPriceDRate(props.periodicReportInfo['priceD-rate']);
    setPriceE(props.periodicReportInfo['priceE']);
    setPriceERate(props.periodicReportInfo['priceE-rate']);
    setPriceF(props.periodicReportInfo['priceF']);
    setPriceFRate(props.periodicReportInfo['priceF-rate']);
    setPriceSchedule(props.periodicReportInfo['price-schedule']);
    setAlternativeFuel1(props.periodicReportInfo['alternative-fuel1']);
    setAlternativeFuel1Rate(props.periodicReportInfo['alternative-fuel1-rate']);
    setAlternativeFuel1Unit(props.periodicReportInfo['alternative-fuel1-unit']);
    setAlternativeFuel1Efficiency(
      props.periodicReportInfo['alternative-fuel1-efficiency']
    );
    setAlternativeFuel1Co2(
      props.periodicReportInfo['alternative-fuel1-co2-emission-factor']
    );
    setAlternativeFuel2(props.periodicReportInfo['alternative-fuel2']);
    setAlternativeFuel2Rate(props.periodicReportInfo['alternative-fuel2-rate']);
    setAlternativeFuel2Unit(props.periodicReportInfo['alternative-fuel2-unit']);
    setAlternativeFuel2Efficiency(
      props.periodicReportInfo['alternative-fuel2-efficiency']
    );
    setAlternativeFuel2Co2(
      props.periodicReportInfo['alternative-fuel2-co2-emission-factor']
    );
  }, [props.periodicReportInfo]);

  /**
   * 電気料金時間表のエラーチェック
   */
  useEffect(() => {
    let priceScheduleError = false;
    for (const month of priceSchedule) {
      for (const day of month) {
        if (day === 'A' && (priceA === '' || priceARate === '')) {
          priceScheduleError = true;
        } else if (day === 'B' && (priceB === '' || priceBRate === '')) {
          priceScheduleError = true;
        } else if (day === 'C' && (priceC === '' || priceCRate === '')) {
          priceScheduleError = true;
        } else if (day === 'D' && (priceD === '' || priceDRate === '')) {
          priceScheduleError = true;
        } else if (day === 'E' && (priceE === '' || priceERate === '')) {
          priceScheduleError = true;
        } else if (day === 'F' && (priceF === '' || priceFRate === '')) {
          priceScheduleError = true;
        }
      }
    }
    setPriceScheduleError(priceScheduleError);
  }, [
    priceSchedule,
    priceA,
    priceARate,
    priceB,
    priceBRate,
    priceC,
    priceCRate,
    priceD,
    priceDRate,
    priceE,
    priceERate,
    priceF,
    priceFRate,
  ]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [props.trans]);

  /** BACKボタン押下時処理 */
  const handleBack = () => {
    const message: string = props.trans(
      'PeriodicReportSetting.popupTxt'
    ) as string;
    const confirmLeave = window.confirm(message);
    if (confirmLeave) {
      navigate(-1); // 前のページに戻る
    }
  };

  /** APPLYボタン有効無効判定 */
  const applyBtnDisabled = () => {
    /** Applyボタン無効フラグ */
    let disabledFlag = false;
    //必須項目が空欄又はテキストボックスがエラーとなっている場合は無効にする
    if (
      props.reportGeneration === 'active' &&
      (serialNumberError ||
        installationDate === null ||
        installationDate.isValid() === false ||
        currency === '' ||
        pricePlanError ||
        priceARate === '' ||
        (priceARate !== '' && priceA === '') ||
        (priceBRate !== '' && priceB === '') ||
        (priceCRate !== '' && priceC === '') ||
        (priceDRate !== '' && priceD === '') ||
        (priceERate !== '' && priceE === '') ||
        (priceFRate !== '' && priceF === '') ||
        pricePlanRateError ||
        priceScheduleError ||
        co2EmissionFactor === '' ||
        co2EmissionFactorError ||
        alternativeFuel1Error ||
        (alternativeFuel1Rate === '' &&
          alternativeFuel1 !== 'electric-heater') ||
        alternativeFuel1RateError ||
        alternativeFuel1Efficiency === '' ||
        alternativeFuel1EfficiencyError ||
        (alternativeFuel1Co2 === '' &&
          alternativeFuel1 !== 'electric-heater') ||
        alternativeFuel1Co2Error ||
        alternativeFuel2Error ||
        (alternativeFuel2Rate === '' &&
          alternativeFuel2 !== 'electric-heater') ||
        alternativeFuel2RateError ||
        alternativeFuel2Efficiency === '' ||
        alternativeFuel2EfficiencyError ||
        (alternativeFuel2Co2 === '' &&
          alternativeFuel2 !== 'electric-heater') ||
        alternativeFuel2Co2Error)
    ) {
      disabledFlag = true;
    }
    return disabledFlag;
  };

  /** Applyボタン押下時処理 */
  const handleApply = () => {
    setAPIWait(true);

    if (installationDate === null) return;
    //APIに渡すデータ
    const periodicReportSetting: apiIF.reqParameters.periodicReportSetting = {
      'report-generation': props.reportGeneration,
      'serial-number': serialNumber,
      'install-date': installationDate.format('YYYY/MM/DD'),
      'co2-emission-factor': co2EmissionFactor,
      'report-issue': reportIssueConvertTable[reportIssueMonth],
      'currency': currency,
      'priceA': priceA,
      'priceA-rate': priceARate,
      'priceB': priceB,
      'priceB-rate': priceBRate,
      'priceC': priceC,
      'priceC-rate': priceCRate,
      'priceD': priceD,
      'priceD-rate': priceDRate,
      'priceE': priceE,
      'priceE-rate': priceERate,
      'priceF': priceF,
      'priceF-rate': priceFRate,
      'price-schedule': priceSchedule,
      'alternative-fuel1': alternativeFuel1,
      'alternative-fuel1-rate': alternativeFuel1Rate,
      'alternative-fuel1-unit': alternativeFuel1Unit,
      'alternative-fuel1-efficiency': alternativeFuel1Efficiency,
      'alternative-fuel1-co2-emission-factor': alternativeFuel1Co2,
      'alternative-fuel2': alternativeFuel2,
      'alternative-fuel2-rate': alternativeFuel2Rate,
      'alternative-fuel2-unit': alternativeFuel2Unit,
      'alternative-fuel2-efficiency': alternativeFuel2Efficiency,
      'alternative-fuel2-co2-emission-factor': alternativeFuel2Co2,
      'refrigerant': props.periodicReportInfo['refrigerant'],
    };

    const patchData: apiIF.reqParameters.patchCompositionsRTUnit = {
      'periodic-report': periodicReportSetting,
    };

    patchCompositionsRTUnit(props.siteID, props.gwID, props.unitID, patchData)
      .then(() => {
        setIsOpenSuccessDialog(true);
        props.setCompositionsInSite(prev => {
          return prev.map(gwInfo => {
            return {
              ...gwInfo,
              'unit-informations': gwInfo['unit-informations'].map(unitInfo => {
                if (unitInfo['unit-id'] === props.unitID) {
                  return {
                    ...unitInfo,
                    'periodic-report': periodicReportSetting,
                  };
                }
                return unitInfo;
              }),
            };
          });
        });
      })
      .catch(err => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * 通貨コードを通貨記号に変換する
   * @param currency 通貨コード
   * @returns 通貨記号
   */
  const currencySign = (currency: string) => {
    switch (currency) {
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      case 'USD':
        return '$';
      case 'JPY':
        return '¥';
      default:
        return currency;
    }
  };

  if (props.reportGeneration === 'inactive') {
    return (
      <>
        {/** ボタン */}
        <Grid
          container
          justifyContent="flex-end"
          spacing={3}
          className="serviceInfo__periodicReportSetting__contents__button">
          <Grid item xs="auto">
            <SecondaryButton
              disabled={apiWait}
              onClick={handleBack}
              buttonText={props.trans('PeriodicReportSetting.buttonTxt.back')}
            />
          </Grid>
          <Grid item xs="auto">
            <PrimaryButton
              onClick={handleApply}
              disabled={applyBtnDisabled() || apiWait}
              buttonText={props.trans('PeriodicReportSetting.buttonTxt.apply')}
              apiWait={apiWait}
            />
          </Grid>
        </Grid>
        <SuccessMessageDialog
          open={isOpenSuccessDialog}
          setOpen={setIsOpenSuccessDialog}
          trans={props.trans}
        />
        <FailMessageDialog
          open={isOpenErrorDialog}
          setOpen={setIsOpenErrorDialog}
          trans={props.trans}
        />
      </>
    );
  }
  return (
    <>
      {/** 一般設定 */}
      <Grid item xs={12} container>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__subTitle">
          <h3>{props.trans('PeriodicReportSetting.heading.commonInfo')}</h3>
        </Grid>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__unitInfo">
          <Stack
            className="serviceInfo__periodicReportSetting__contents__unitInfo__space"
            spacing={5}>
            {/** レポートの発行月 */}
            <TextFieldSelectBox
              choices={pullDownMenu.REPORT_ISSUE_THREE_MONTH}
              setValue={setReportIssueMonth}
              id={`selectReportIssue`}
              label={`${props.trans(
                'PeriodicReportSetting.reportIssueMonth'
              )}*`}
              value={reportIssueMonth}
              class="serviceInfo__periodicReportSetting__contents__unitInfo__textFieldSelectBox"
            />
            {/** ユニット型式 */}
            <TextField
              id="nameplateQton"
              className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
              label={props.trans('PeriodicReportSetting.unitModel')}
              value={props.nameplate}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
            />
            {/** 電源電圧 */}
            <TextField
              id="periodicReportSetting-voltage"
              className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
              label={props.trans('PeriodicReportSetting.voltage')}
              value={
                props.inputVoltage === '' || props.inputVoltage === undefined
                  ? props.trans('PeriodicReportSetting.noSetting')
                  : props.inputVoltage
              }
              InputProps={
                props.inputVoltage === '' || props.inputVoltage === undefined
                  ? {readOnly: true}
                  : {
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">V</InputAdornment>
                      ),
                    }
              }
              variant="standard"
            />

            {/** 製造番号入力欄 */}
            <ChkInputTextbox
              setValue={setSerialNumber}
              setError={setSerialNumberError}
              chkInputTxtOK={format.PERIODIC_REPORT_SETTING_SERIAL_NUMBER_OK}
              type="text"
              label={props.trans('PeriodicReportSetting.serialNumber')}
              value={serialNumber}
              id="mv-inputApplySerialNumber"
              class="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
              helperText=" "
              errHelperText={props.trans(
                'PeriodicReportSetting.txtField.errIncorrectEntry'
              )}
            />
            {/** 据付日入力欄 */}
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={decideDayjsDatePickerLocale(language)}>
              <DatePicker
                className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
                //prettier-ignore
                label={`${props.trans('PeriodicReportSetting.installationDate')}*`}
                onChange={newValue => setInstallationDate(newValue)}
                slotProps={{
                  textField: {
                    error:
                      installationDate === null ||
                      installationDate.isValid() === false,
                    helperText:
                      installationDate === null ||
                      installationDate.isValid() === false
                        ? props.trans(
                            'PeriodicReportSetting.txtField.errIncorrectEntry'
                          )
                        : ' ',
                  },
                }}
                value={installationDate}
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>
          </Stack>
        </Grid>
      </Grid>
      {/** 電気料金設定 */}
      <Grid item xs={12} container>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__subTitle">
          <h3>
            {props.trans('PeriodicReportSetting.heading.electricBillInfo')}
          </h3>
        </Grid>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__electricBillInfo">
          <Box className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space">
            {/** 通貨 */}
            <TextFieldSelectBox
              choices={pullDownMenu.ELECTRIC_CURRENCY}
              setValue={setCurrency}
              id={'selectCurrency'}
              label={`${props.trans('PeriodicReportSetting.currency')}*`}
              value={currency}
              class="serviceInfo__periodicReportSetting__contents__electricBillInfo__textFieldSelectBox--currency"
            />
          </Box>
          <Box className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space">
            <h3>
              {props.trans('PeriodicReportSetting.varietyElectric.title')}
            </h3>
            <div>
              &nbsp;
              {props.trans('PeriodicReportSetting.varietyElectric.abstract')}
            </div>
          </Box>
          <Stack
            className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space"
            spacing={3}>
            {/** 料金プランA */}
            <PriceRateComponent
              price={'priceA'}
              priceName={priceA}
              priceRate={priceARate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceA}
              setPriceRate={setPriceARate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
            {/** 料金プランB */}
            <PriceRateComponent
              price={'priceB'}
              priceName={priceB}
              priceRate={priceBRate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceB}
              setPriceRate={setPriceBRate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
            {/** 料金プランC */}
            <PriceRateComponent
              price={'priceC'}
              priceName={priceC}
              priceRate={priceCRate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceC}
              setPriceRate={setPriceCRate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
            {/** 料金プランD */}
            <PriceRateComponent
              price={'priceD'}
              priceName={priceD}
              priceRate={priceDRate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceD}
              setPriceRate={setPriceDRate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
            {/** 料金プランE */}
            <PriceRateComponent
              price={'priceE'}
              priceName={priceE}
              priceRate={priceERate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceE}
              setPriceRate={setPriceERate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
            {/** 料金プランF */}
            <PriceRateComponent
              price={'priceF'}
              priceName={priceF}
              priceRate={priceFRate}
              currencySign={currencySign(currency)}
              setPriceName={setPriceF}
              setPriceRate={setPriceFRate}
              setPricePlanError={setPricePlanError}
              setPricePlanRateError={setPricePlanRateError}
              trans={props.trans}
            />
          </Stack>
          <Box className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space">
            <h3>
              {props.trans(`PeriodicReportSetting.timeZoneElectric.title`)}
            </h3>
            <div>
              &nbsp;
              {props.trans(`PeriodicReportSetting.timeZoneElectric.abstract`)}
            </div>
          </Box>
          <Box className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space">
            {/** 電気料金タイムスケジュール */}
            <PriceScheduleComponent
              priceARate={priceARate}
              priceBRate={priceBRate}
              priceCRate={priceCRate}
              priceDRate={priceDRate}
              priceERate={priceERate}
              priceFRate={priceFRate}
              priceSchedule={priceSchedule}
              pricePlanError={pricePlanError}
              priceScheduleError={priceScheduleError}
              setPriceSchedule={setPriceSchedule}
              setPricePlanError={setPricePlanError}
              trans={props.trans}
            />
          </Box>
          <Box className="serviceInfo__periodicReportSetting__contents__electricBillInfo__space--co2EmissionFactor">
            {/** CO2排出係数 */}
            <ChkInputTextbox
              setValue={setCo2EmissionFactor}
              setError={setCo2EmissionFactorError}
              chkInputTxtOK={
                format.PERIODIC_REPORT_SETTING_CO2_EMISSION_PATTERN_OK
              }
              chkInputTxtError={/^\s*$/}
              type="text"
              label={`${props.trans(
                'PeriodicReportSetting.electricCO2EmissionFactor'
              )}*`}
              value={co2EmissionFactor}
              id="mv-inputApplyCo2EmissionFactor"
              class="serviceInfo__periodicReportSetting__contents__electricBillInfo__chkInputTextBox--co2EmissionFactor"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {props.trans(
                      'PeriodicReportSetting.unit.electricCO2EmissionFactor'
                    )}
                  </InputAdornment>
                ),
              }}
              helperText=" "
              errHelperText={
                co2EmissionFactor === ''
                  ? props.trans('PeriodicReportSetting.txtField.errNoInput')
                  : props.trans(
                      'PeriodicReportSetting.txtField.errIncorrectEntry'
                    )
              }
            />
          </Box>
        </Grid>
      </Grid>
      {/** 代替燃料設定 */}
      <Grid item xs={12} container>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__subTitle">
          <h3>
            {props.trans('PeriodicReportSetting.heading.alternativeFuelInfo')}
          </h3>
        </Grid>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__alternativeFuel">
          <Stack
            className="serviceInfo__periodicReportSetting__contents__alternativeFuel__space"
            spacing={2}>
            {/** 代替燃料1 */}
            <AlternativeFuelComponent
              number="1"
              alternativeFuel={alternativeFuel1}
              alternativeFuelRate={alternativeFuel1Rate}
              alternativeFuelUnit={alternativeFuel1Unit}
              alternativeFuelEfficiency={alternativeFuel1Efficiency}
              alternativeFuelCo2={alternativeFuel1Co2}
              currencySign={currencySign(currency)}
              setAlternativeFuel={setAlternativeFuel1}
              setAlternativeFuelRate={setAlternativeFuel1Rate}
              setAlternativeFuelUnit={setAlternativeFuel1Unit}
              setAlternativeFuelEfficiency={setAlternativeFuel1Efficiency}
              setAlternativeFuelCo2={setAlternativeFuel1Co2}
              setAlternativeFuelError={setAlternativeFuel1Error}
              setAlternativeFuelRateError={setAlternativeFuel1RateError}
              setAlternativeFuelEfficiencyError={
                setAlternativeFuel1EfficiencyError
              }
              setAlternativeFuelCo2Error={setAlternativeFuel1Co2Error}
              trans={props.trans}
            />
            {/** 代替燃料2 */}
            <AlternativeFuelComponent
              number="2"
              alternativeFuel={alternativeFuel2}
              alternativeFuelRate={alternativeFuel2Rate}
              alternativeFuelUnit={alternativeFuel2Unit}
              alternativeFuelEfficiency={alternativeFuel2Efficiency}
              alternativeFuelCo2={alternativeFuel2Co2}
              currencySign={currencySign(currency)}
              setAlternativeFuel={setAlternativeFuel2}
              setAlternativeFuelRate={setAlternativeFuel2Rate}
              setAlternativeFuelUnit={setAlternativeFuel2Unit}
              setAlternativeFuelEfficiency={setAlternativeFuel2Efficiency}
              setAlternativeFuelCo2={setAlternativeFuel2Co2}
              setAlternativeFuelError={setAlternativeFuel2Error}
              setAlternativeFuelRateError={setAlternativeFuel2RateError}
              setAlternativeFuelEfficiencyError={
                setAlternativeFuel2EfficiencyError
              }
              setAlternativeFuelCo2Error={setAlternativeFuel2Co2Error}
              trans={props.trans}
            />
          </Stack>
        </Grid>
      </Grid>
      {/** ボタン */}
      <Grid
        container
        justifyContent="flex-end"
        spacing={3}
        className="serviceInfo__periodicReportSetting__contents__button">
        <Grid item xs="auto">
          <SecondaryButton
            disabled={apiWait}
            onClick={handleBack}
            buttonText={props.trans('PeriodicReportSetting.buttonTxt.back')}
          />
        </Grid>
        <Grid item xs="auto">
          <PrimaryButton
            onClick={handleApply}
            disabled={applyBtnDisabled() || apiWait}
            buttonText={props.trans('PeriodicReportSetting.buttonTxt.apply')}
            apiWait={apiWait}
          />
        </Grid>
      </Grid>
      <SuccessMessageDialog
        open={isOpenSuccessDialog}
        setOpen={setIsOpenSuccessDialog}
        trans={props.trans}
      />
      <FailMessageDialog
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        trans={props.trans}
      />
    </>
  );
};

/**
 * 料金プラン背景色決定関数
 * @param price 電気料金プランの種類
 * @returns クラス名
 */
const decidePlanBackgroundColor = (pricePlan: string) => {
  switch (pricePlan) {
    case 'priceA':
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceA';
    case 'priceB':
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceB';
    case 'priceC':
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceC';
    case 'priceD':
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceD';
    case 'priceE':
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceE';
    case 'priceF':
    default:
      return 'serviceInfo__periodicReportSetting__contents__electricBillInfo__planBackgroundColor--priceF';
  }
};

/**
 * 電気料金コンポーネント
 * @param props props
 * @returns JSX.Element
 */
const PriceRateComponent = (props: {
  /** 電気料金プラン */
  price: 'priceA' | 'priceB' | 'priceC' | 'priceD' | 'priceE' | 'priceF';
  /** 電気料金プラン名 */
  priceName: string;
  /** 電気料金単価 */
  priceRate: string;
  /** 通貨記号 */
  currencySign: string;
  /** 電気料金プラン名設定関数 */
  setPriceName: Dispatch<SetStateAction<string>>;
  /** 電気料金単価設定関数 */
  setPriceRate: Dispatch<SetStateAction<string>>;
  /** 電気料金プラン名エラー設定関数 */
  setPricePlanError: Dispatch<SetStateAction<boolean>>;
  /** 単価エラー設定関数 */
  setPricePlanRateError: Dispatch<SetStateAction<boolean>>;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  return (
    <Grid container alignItems="center" columnSpacing={4}>
      <Grid item xs="auto">
        <Box
          className={`serviceInfo__periodicReportSetting__contents__electricBillInfo__planIcon ${decidePlanBackgroundColor(
            props.price
          )}`}>
          {props.trans(`PeriodicReportSetting.plan.${props.price}`)}
        </Box>
      </Grid>
      <Grid
        item
        xs="auto"
        className="serviceInfo__periodicReportSetting__contents__electricBillInfo__text">
        :
      </Grid>
      <Grid item xs="auto">
        {/* 電気料金プラン名 */}
        <ChkInputTextbox
          setValue={props.setPriceName}
          setError={props.setPricePlanError}
          chkInputTxtError={format.BASIC_PATTERN_ERROR}
          chkLength={
            format.PERIODIC_REPORT_SETTING_ELECTRICITY_UNIT_PRICE_OF_NAME_LENGTH_MAX
          }
          label={
            props.price === 'priceA'
              ? `${props.trans(`PeriodicReportSetting.label.priceName`)}*`
              : props.trans(`PeriodicReportSetting.label.priceName`)
          }
          value={props.priceName}
          id={`mv-price${props.price}NameInputBox`}
          class="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceName"
          helperText=" "
          errHelperText={props.trans(
            'PeriodicReportSetting.txtField.errIncorrectEntry'
          )}
        />
      </Grid>
      <Grid item xs="auto" />
      <Grid item>
        {/* 電気料金単価 */}
        <ChkInputTextbox
          setValue={props.setPriceRate}
          setError={props.setPricePlanRateError}
          chkInputTxtOK={format.PERIODIC_REPORT_SETTING_PRICE_RATE_OK}
          chkInputTxtError={props.price === 'priceA' ? /^\s*$/ : undefined}
          label={
            props.price === 'priceA'
              ? `${props.trans(`PeriodicReportSetting.label.priceRate`)}*`
              : props.trans(`PeriodicReportSetting.label.priceRate`)
          }
          value={props.priceRate}
          id={`mv-price${props.price}priceRateInputBox`}
          class="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceRate"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {props.trans('PeriodicReportSetting.unit.priceRate', {
                  currency: props.currencySign,
                })}
              </InputAdornment>
            ),
          }}
          helperText=" "
          errHelperText={props.trans(
            props.price === 'priceA' && props.priceRate === ''
              ? props.trans('PeriodicReportSetting.txtField.errNoInput')
              : props.trans('PeriodicReportSetting.txtField.errIncorrectEntry')
          )}
        />
      </Grid>
    </Grid>
  );
};

/**
 * 電気料金スケジュールコンポーネント
 * @param props props
 * @returns JSX.Element
 */
const PriceScheduleComponent = (props: {
  /** 電気料金プランA単価 */
  priceARate: apiIF.reqParameters.periodicReportSetting['priceA-rate'];
  /** 電気料金プランB単価 */
  priceBRate: apiIF.reqParameters.periodicReportSetting['priceB-rate'];
  /** 電気料金プランC単価 */
  priceCRate: apiIF.reqParameters.periodicReportSetting['priceC-rate'];
  /** 電気料金プランD単価 */
  priceDRate: apiIF.reqParameters.periodicReportSetting['priceD-rate'];
  /** 電気料金プランE単価 */
  priceERate: apiIF.reqParameters.periodicReportSetting['priceE-rate'];
  /** 電気料金プランF単価 */
  priceFRate: apiIF.reqParameters.periodicReportSetting['priceF-rate'];
  /** 電気料金スケジュール */
  priceSchedule: apiIF.reqParameters.periodicReportSetting['price-schedule'];
  /** 電気料金スケジュール入力欄エラー */
  priceScheduleError: boolean;
  /** 電気料金プラン入力欄エラー */
  pricePlanError: boolean;
  /**電気料金スケジュール設定関数 */
  setPriceSchedule: Dispatch<
    SetStateAction<apiIF.reqParameters.periodicReportSetting['price-schedule']>
  >;
  /** 電気料金プラン名エラー設定関数 */
  setPricePlanError: Dispatch<boolean>;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  //prettier-ignore
  const [pressedPriceButton, setPressedPriceButton] = useState<'priceA' | 'priceB' | 'priceC' | 'priceD' | 'priceE' | 'priceF' | ''>(''); //選択中の電気料金プラン。初期値は選択なし。
  //prettier-ignore
  const [priceSchedule, setPriceSchedule] = useState<apiIF.reqParameters.periodicReportSetting['price-schedule']>(props.priceSchedule);

  useEffect(() => {
    setPriceSchedule(props.priceSchedule);
  }, [props.priceSchedule]);

  /**
   * 電気料金スケジュール修正処理
   * @param month     選択されたセル(月)
   * @param hour      選択されたセル(時間)
   */
  const settingPriceSchedule = (month: number, hour: number) => {
    //電気料金プランが選択されていない場合は処理を行わない
    if (pressedPriceButton === '') return;

    const newPriceSchedule = structuredClone(priceSchedule);

    switch (pressedPriceButton) {
      case 'priceA':
        newPriceSchedule[month][hour] = 'A';
        break;
      case 'priceB':
        newPriceSchedule[month][hour] = 'B';
        break;
      case 'priceC':
        newPriceSchedule[month][hour] = 'C';
        break;
      case 'priceD':
        newPriceSchedule[month][hour] = 'D';
        break;
      case 'priceE':
        newPriceSchedule[month][hour] = 'E';
        break;
      case 'priceF':
        newPriceSchedule[month][hour] = 'F';
        break;
    }

    setPriceSchedule(newPriceSchedule);
    props.setPriceSchedule(newPriceSchedule);
  };

  /**
   * 電気料金スケジュール表のセルドラッグ時処理
   * @param event     イベント
   * @param month     ドラッグしたセルの行の月
   * @param hour      ドラッグしたセルの時間
   */
  const onMouseOverFunc = (
    event: React.MouseEvent<HTMLTableCellElement>,
    month: number,
    hour: number
  ) => {
    if (event.buttons === 1) {
      settingPriceSchedule(month, hour);
    }
  };

  /**
   * 電気料金プランの単価が未入力か判断する関数
   * @param pricePlan 対象の電気料金プラン
   * @returns ボタン操作不可フラグ
   */
  const checkPlanRate = (
    pricePlan:
      | 'priceA'
      | 'priceB'
      | 'priceC'
      | 'priceD'
      | 'priceE'
      | 'priceF'
      | ''
  ) => {
    /** 単価未設定:true, 単価設定済み:false */
    let disabledButtonFlag = false;
    //対象のプランの単価が未設定の場合、ボタン操作不可フラグをONにする
    switch (pricePlan) {
      case 'priceA':
        if (props.priceARate === '') {
          disabledButtonFlag = true;
        }
        break;
      case 'priceB':
        if (props.priceBRate === '') {
          disabledButtonFlag = true;
        }
        break;
      case 'priceC':
        if (props.priceCRate === '') {
          disabledButtonFlag = true;
        }
        break;
      case 'priceD':
        if (props.priceDRate === '') {
          disabledButtonFlag = true;
        }
        break;
      case 'priceE':
        if (props.priceERate === '') {
          disabledButtonFlag = true;
        }
        break;
      case 'priceF':
        if (props.priceFRate === '') {
          disabledButtonFlag = true;
        }
        break;
      default:
        disabledButtonFlag = true;
        break;
    }
    return disabledButtonFlag;
  };

  /**
   * 電気料金プラン選択ボタンコンポーネント
   * @param pricerPlan 料金プラン
   * @return JSX
   */
  const SelectPriceButton = (
    pricePlan:
      | 'priceA'
      | 'priceB'
      | 'priceC'
      | 'priceD'
      | 'priceE'
      | 'priceF'
      | ''
  ) => (
    <>
      <Grid item xs="auto">
        <Button
          color="inherit"
          variant="contained"
          onClick={() => setPressedPriceButton(pricePlan)}
          disabled={checkPlanRate(pricePlan) === true}
          className={
            pricePlan === pressedPriceButton
              ? decidePlanBackgroundColor(pricePlan)
              : 'serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__button'
          }>
          {props.trans(`PeriodicReportSetting.plan.${pricePlan}`)}
        </Button>
      </Grid>
    </>
  );

  return (
    <>
      <Box>
        <div className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__errText">
          {props.priceScheduleError === true
            ? props.trans(`PeriodicReportSetting.errMsg.priceScheduleErr`)
            : ' '}
        </div>
      </Box>
      {/** 電気料金スケジュール */}
      <TableContainer>
        <Table className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__table">
          <TableHead>
            <TableRow>
              {/** 表左上の空白セル */}
              <TableCell className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__table__head--first" />
              {/** 1時間ごとの表記 */}
              {[...Array(24)].map((_, i) => (
                <TableCell
                  align="center"
                  key={`cell-header-${i}`}
                  className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__table__head">
                  {i}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(priceSchedule.length)].map((_, month) => (
              <TableRow key={`row-${month}`}>
                <TableCell
                  key={`cell-${month}`}
                  className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__table__body__monthCell">
                  {props.trans(`PeriodicReportSetting.month.${month}`)}
                </TableCell>
                {priceSchedule[month].map((plan, hour) => {
                  return (
                    //プランによって背景色が変化
                    //クリックorドラッグでセルごとに選択中のプランに変更
                    <TableCell
                      key={`cell-${month}-${hour}`}
                      onMouseOver={event => onMouseOverFunc(event, month, hour)}
                      onMouseDown={() => settingPriceSchedule(month, hour)}
                      className={`serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__table__body__cell ${decidePlanBackgroundColor(
                        'price' + plan.toString()
                      )}`}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/** 料金プラン選択ボタン */}
      <Grid
        container
        justifyContent="space-around"
        className="serviceInfo__periodicReportSetting__contents__electricBillInfo__priceSchedule__button">
        {SelectPriceButton('priceA')}
        {SelectPriceButton('priceB')}
        {SelectPriceButton('priceC')}
        {SelectPriceButton('priceD')}
        {SelectPriceButton('priceE')}
        {SelectPriceButton('priceF')}
      </Grid>
    </>
  );
};

/**
 * 代替燃料情報欄表示コンポーネント
 * @param props props
 * @return JSX.Element
 */
const AlternativeFuelComponent = (props: {
  /** 代替燃料番号 */
  number: string;
  /** 代替燃料情報 */
  alternativeFuel: apiIF.reqParameters.periodicReportSetting['alternative-fuel1'];
  /** 代替燃料1単価 */
  alternativeFuelRate: apiIF.reqParameters.periodicReportSetting['alternative-fuel1-rate'];
  /** 代替燃料1単位 */
  alternativeFuelUnit: apiIF.reqParameters.periodicReportSetting['alternative-fuel1-unit'];
  /** 代替燃料1システム効率 */
  alternativeFuelEfficiency: apiIF.reqParameters.periodicReportSetting['alternative-fuel1-efficiency'];
  /** 代替燃料1用CO2排出係数 */
  alternativeFuelCo2: apiIF.reqParameters.periodicReportSetting['alternative-fuel1-co2-emission-factor'];
  /** 通貨記号 */
  currencySign: string;
  /** 代替燃料情報設定関数 */
  setAlternativeFuel: Dispatch<
    SetStateAction<
      apiIF.reqParameters.periodicReportSetting['alternative-fuel1']
    >
  >;
  /** 代替燃料1単価設定関数 */
  setAlternativeFuelRate: Dispatch<
    SetStateAction<
      apiIF.reqParameters.periodicReportSetting['alternative-fuel1-rate']
    >
  >;
  /** 代替燃料1単位設定関数 */
  setAlternativeFuelUnit: Dispatch<
    SetStateAction<
      apiIF.reqParameters.periodicReportSetting['alternative-fuel1-unit']
    >
  >;
  /** 代替燃料1システム効率設定関数 */
  setAlternativeFuelEfficiency: Dispatch<
    SetStateAction<
      apiIF.reqParameters.periodicReportSetting['alternative-fuel1-efficiency']
    >
  >;
  /** 代替燃料1用CO2排出係数設定関数 */
  setAlternativeFuelCo2: Dispatch<
    SetStateAction<
      apiIF.reqParameters.periodicReportSetting['alternative-fuel1-co2-emission-factor']
    >
  >;
  /** 代替燃料1エラー有無設定関数 */
  setAlternativeFuelError: Dispatch<SetStateAction<boolean>>;
  /** 代替燃料1単価エラー有無設定関数 */
  setAlternativeFuelRateError: Dispatch<SetStateAction<boolean>>;
  /** 代替燃料1システム効率エラー有無設定関数 */
  setAlternativeFuelEfficiencyError: Dispatch<SetStateAction<boolean>>;
  /** 代替燃料1CO2排出係数エラー有無設定関数 */
  setAlternativeFuelCo2Error: Dispatch<SetStateAction<boolean>>;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  //代替燃料単位の選択肢一覧
  const [unitPullDownMenu, setUnitPullDownMenu] = useState(
    pullDownMenu.FUEL_UNIT_PRICE
  );

  //代替燃料変更時、代替燃料単位と単位の選択肢を変更する
  useEffect(() => {
    const menu = pullDownMenu.FUEL_UNIT_PRICE;
    switch (props.alternativeFuel) {
      case 'heavy-oil':
        //単位が変わらない場合は、CO2排出係数を変更する
        if (props.alternativeFuelUnit === 'L') {
          props.setAlternativeFuelCo2('2.71');
        } else {
          props.setAlternativeFuelUnit('L');
        }
        setUnitPullDownMenu(
          //単位の選択肢をLに絞る
          menu.filter(item => {
            return item.value === 'L';
          })
        );
        break;
      case 'kerosene':
        //単位が変わらない場合はCO2排出係数を変更する
        if (props.alternativeFuelUnit === 'L') {
          props.setAlternativeFuelCo2('2.49');
        } else {
          props.setAlternativeFuelUnit('L');
        }
        setUnitPullDownMenu(
          //単位の選択肢をLに絞る
          menu.filter(item => {
            return item.value === 'L';
          })
        );
        break;
      case 'electric-heater':
        props.setAlternativeFuelRate('');
        //単位が変わらない場合はCO2排出係数を変更する
        if (props.alternativeFuelUnit === 'kWh') {
          props.setAlternativeFuelCo2('');
        } else {
          props.setAlternativeFuelUnit('kWh');
        }
        setUnitPullDownMenu(
          //単位の選択肢をkWhに絞る
          menu.filter(item => {
            return item.value === 'kWh';
          })
        );
        break;
      case 'city-gas':
        //単位が変わらない場合はCO2排出係数を変更する
        if (props.alternativeFuelUnit === 'm3') {
          props.setAlternativeFuelCo2('2.16');
        } else {
          props.setAlternativeFuelUnit('m3');
        }
        setUnitPullDownMenu(
          //単位の選択肢を㎥とkWhに絞る
          menu.filter(item => {
            return item.value === 'm3' || item.value === 'kWh';
          })
        );
        break;
      case 'lpg':
        //単位が変わらない場合はCO2排出係数を変更する
        if (props.alternativeFuelUnit === 'm3') {
          props.setAlternativeFuelCo2('6.55');
        } else {
          props.setAlternativeFuelUnit('m3');
        }
        setUnitPullDownMenu(
          //単位の選択肢を㎥とLとkgに絞る
          menu.filter(item => {
            return (
              item.value === 'm3' || item.value === 'L' || item.value === 'kg'
            );
          })
        );
        break;
      case 'lng':
        props.setAlternativeFuelUnit('1000000BTU');
        setUnitPullDownMenu(
          //単位の選択肢を1000000BTUとkgに絞る
          menu.filter(item => {
            return item.value === '1000000BTU' || item.value === 'kg';
          })
        );
        break;
    }
  }, [props.alternativeFuel]);

  //代替燃料の単位変更時、代替燃料用CO2排出係数を各単位ごとの初期値に設定する。
  useEffect(() => {
    switch (props.alternativeFuelUnit) {
      case 'L':
        //代替燃料がA重油の場合
        if (props.alternativeFuel === 'heavy-oil') {
          props.setAlternativeFuelCo2('2.71');
          //代替燃料が灯油の場合
        } else if (props.alternativeFuel === 'kerosene') {
          props.setAlternativeFuelCo2('2.49');
          //代替燃料がLPGの場合
        } else {
          props.setAlternativeFuelCo2('1.59');
        }
        break;
      case 'm3':
        //代替燃料がLPGの場合
        if (props.alternativeFuel === 'lpg') {
          props.setAlternativeFuelCo2('6.55');
          //代替燃料が都市ガスの場合
        } else {
          props.setAlternativeFuelCo2('2.16');
        }
        break;
      case 'kg':
        //代替燃料がLPGの場合
        if (props.alternativeFuel === 'lpg') {
          props.setAlternativeFuelCo2('3');
          //代替燃料がLNGの場合
        } else {
          props.setAlternativeFuelCo2('2.7');
        }
        break;
      case 'kWh':
        //代替燃料が都市ガスの場合
        if (props.alternativeFuel === 'city-gas') {
          props.setAlternativeFuelCo2('0.18');
          //代替燃料が電気ヒータの場合
        } else {
          props.setAlternativeFuelCo2('');
        }
        break;
      case '1000000BTU':
        props.setAlternativeFuelCo2('52.13');
    }
  }, [props.alternativeFuelUnit]);

  return (
    <Grid container alignItems="center" columnSpacing={3}>
      {/** 代替燃料 */}
      <Grid item xs="auto">
        <TextFieldSelectBox
          choices={pullDownMenu.ALTERNATIVE_FUEL}
          setValue={props.setAlternativeFuel}
          id={`selectAlternativeFuel${props.number}`}
          label={`${props.trans(
            `PeriodicReportSetting.label.alternativeFuel${props.number}`
          )}*`}
          value={props.alternativeFuel}
          class={
            'serviceInfo__periodicReportSetting__contents__alternativeFuel__textFieldSelect--alternativeFuel'
          }
          helperText=" "
        />
      </Grid>
      {/** 単価 */}
      <Grid item xs="auto">
        <ChkInputTextbox
          setValue={props.setAlternativeFuelRate}
          setError={props.setAlternativeFuelError}
          chkInputTxtOK={
            format.PERIODIC_REPORT_SETTING_ALTERNATIVE_FUEL_RATE_OK
          }
          chkInputTxtError={
            props.alternativeFuel !== 'electric-heater' ? /^\s*$/ : undefined
          }
          type="text"
          label={
            props.alternativeFuel === 'electric-heater'
              ? props.trans(`PeriodicReportSetting.label.alternativeFuelRate`)
              : `${props.trans(
                  `PeriodicReportSetting.label.alternativeFuelRate`
                )}*`
          }
          value={props.alternativeFuelRate}
          id={`mv-setAlternativeFuel${props.number}Rate`}
          class={
            'serviceInfo__periodicReportSetting__contents__alternativeFuel__chkInputTextBox--rate'
          }
          helperText=" "
          errHelperText={
            props.alternativeFuel !== 'electric-heater' &&
            props.alternativeFuelRate === ''
              ? props.trans('PeriodicReportSetting.txtField.errNoInput')
              : props.trans('PeriodicReportSetting.txtField.errIncorrectEntry')
          }
          disabled={props.alternativeFuel === 'electric-heater'}
        />
      </Grid>
      {/** 代替燃料単位 */}
      <Grid item xs="auto">
        <TextFieldSelectBox
          label={`${props.trans(
            `PeriodicReportSetting.label.alternativeFuelUnit`
          )}*`}
          choices={unitPullDownMenu}
          setValue={props.setAlternativeFuelUnit}
          id={`selectAlternativeFuel${props.number}Unit`}
          value={props.alternativeFuelUnit}
          class={
            'serviceInfo__periodicReportSetting__contents__alternativeFuel__textFieldSelect--unit'
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {props.trans(
                  'PeriodicReportSetting.label.alternativeFuelCurrencySign',
                  {
                    currency: props.currencySign,
                  }
                )}
              </InputAdornment>
            ),
          }}
          helperText=" "
        />
      </Grid>
      {/** 代替燃料システム効率 */}
      <Grid
        item
        xs="auto"
        className="serviceInfo__periodicReportSetting__contents__alternativeFuel__unit">
        <ChkInputTextbox
          setValue={props.setAlternativeFuelEfficiency}
          setError={props.setAlternativeFuelError}
          chkInputTxtOK={format.PERIODIC_REPORT_SETTING_SYSTEM_EFFICIENCY_OK}
          chkInputTxtError={/^\s*$/}
          type="text"
          label={`${props.trans(
            `PeriodicReportSetting.label.systemEfficiency`
          )}*`}
          value={props.alternativeFuelEfficiency}
          id={`mv-alternativeFuel${props.number}systemEfficiency`}
          class={
            'serviceInfo__periodicReportSetting__contents__alternativeFuel__chkInputTextBox--systemEfficiency'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {props.trans('PeriodicReportSetting.unit.amount')}
              </InputAdornment>
            ),
          }}
          helperText=" "
          errHelperText={
            props.alternativeFuelEfficiency === ''
              ? props.trans('PeriodicReportSetting.txtField.errNoInput')
              : props.trans('PeriodicReportSetting.txtField.errIncorrectEntry')
          }
        />
      </Grid>
      {/** 代替燃料CO2排出係数 */}
      <Grid
        item
        className="serviceInfo__periodicReportSetting__contents__alternativeFuel__unit">
        <ChkInputTextbox
          setValue={props.setAlternativeFuelCo2}
          setError={props.setAlternativeFuelError}
          chkInputTxtOK={
            format.PERIODIC_REPORT_SETTING_ALTERNATIVE_CO2_EMISSION_PATTERN_OK
          }
          //代替燃料がelectric-heater以外の場合、空欄を許可しない
          chkInputTxtError={
            props.alternativeFuel !== 'electric-heater' ? /^\s*$/ : undefined
          }
          type="text"
          label={
            props.alternativeFuel === 'electric-heater'
              ? props.trans(`PeriodicReportSetting.label.co2EmissionFactor`)
              : `${props.trans(
                  `PeriodicReportSetting.label.co2EmissionFactor`
                )}*`
          }
          value={props.alternativeFuelCo2}
          id={`mv-alternativeFuel${props.number}CO2EmissionFactor`}
          class={
            'serviceInfo__periodicReportSetting__contents__alternativeFuel__chkInputTextBox--co2EmissionFactor'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {props.trans('PeriodicReportSetting.unit.co2EmissionFactor', {
                  altUnit: props.alternativeFuelUnit,
                })}
              </InputAdornment>
            ),
          }}
          helperText=" "
          errHelperText={
            props.alternativeFuelCo2 === ''
              ? props.trans('PeriodicReportSetting.txtField.errNoInput')
              : props.trans('PeriodicReportSetting.txtField.errIncorrectEntry')
          }
          disabled={props.alternativeFuel === 'electric-heater'}
        />
      </Grid>
    </Grid>
  );
};

/**
 * Hydrolution PROの定期レポート設定画面コンポーネント
 * @param props props
 * @returns JSX.Element
 * */
const ChillerReportSetting = (props: {
  /** サイトID */
  siteID: string;
  /** ゲートウェイID */
  gwID: string;
  /** ユニットID */
  unitID: string;
  /** 銘鈑 */
  nameplate: apiIF.resParameters.rtUnitInformations['nameplate'];
  /** 入力電圧 */
  inputVoltage: apiIF.resParameters.rtUnitInformations['input-voltage'];
  /** レポート生成有無 */
  reportGeneration: apiIF.reqParameters.periodicReportSetting['report-generation'];
  /** 選択したユニットの定期レポート情報 */
  periodicReportInfo: apiIF.resParameters.periodicReportSetting;
  //prettier-ignore
  /** GW毎の熱源機の情報（set関数） */
  setCompositionsInSite: React.Dispatch<React.SetStateAction<apiIF.res.getCompositionsRTUnit[]>>;
  /** 翻訳関数 */
  trans: TFunction;
}) => {
  //prettier-ignore
  const [serialNumber, setSerialNumber] = useState<apiIF.reqParameters.periodicReportSetting['serial-number']>(props.periodicReportInfo['serial-number']); //シリアル番号
  //prettier-ignore
  const [installationDate, setInstallationDate] = useState<Dayjs | null>(dayjs(props.periodicReportInfo['install-date'])); //納入日
  //prettier-ignore
  const [co2EmissionFactor, setCo2EmissionFactor] = useState<apiIF.reqParameters.periodicReportSetting['co2-emission-factor'] >(props.periodicReportInfo['co2-emission-factor']); //CO2排出係数
  //prettier-ignore
  const [refrigerant, setRefrigerant] = useState<apiIF.reqParameters.periodicReportSetting['refrigerant']>(props.periodicReportInfo['refrigerant']); //冷媒
  //TextBoxのエラー状態
  //prettier-ignore
  const [serialNumberError, setSerialNumberError] = useState(false); //製造番号
  //prettier-ignore
  const [co2EmissionFactorError, setCo2EmissionFactorError] = useState(false); //CO2排出係数
  //API関連
  //prettier-ignore
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  //prettier-ignore
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  //prettier-ignore
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState(false); //API成功時メッセージ表示

  useEffect(() => {
    setSerialNumber(props.periodicReportInfo['serial-number']);
    setInstallationDate(
      dayjs(
        props.periodicReportInfo['install-date'] === ''
          ? undefined
          : props.periodicReportInfo['install-date']
      )
    );
    setCo2EmissionFactor(props.periodicReportInfo['co2-emission-factor']);
    setRefrigerant(props.periodicReportInfo['refrigerant']);
  }, [props.periodicReportInfo]);

  const navigate = useNavigate();

  /** BACKボタン押下時処理 */
  const handleBack = () => {
    const message: string = props.trans(
      'PeriodicReportSetting.popupTxt'
    ) as string;
    const confirmLeave = window.confirm(message);
    if (confirmLeave) {
      navigate(-1); // 前のページに戻る
    }
  };

  /** APPLYボタン有効無効判定 */
  const applyBtnDisabled = () => {
    /** Applyボタン無効フラグ */
    let disabledFlag = false;
    //テキストボックスがエラーとなっている場合は無効にする
    if (
      props.reportGeneration === 'active' &&
      (installationDate === null ||
        installationDate.isValid() === false ||
        serialNumberError ||
        co2EmissionFactor === '' ||
        co2EmissionFactorError)
    ) {
      disabledFlag = true;
    }
    return disabledFlag;
  };

  /** Applyボタン押下時処理 */
  const handleApply = () => {
    setAPIWait(true);

    if (installationDate === null) return;
    //APIに渡すデータ
    const periodicReportSetting: apiIF.reqParameters.periodicReportSetting = {
      'report-generation': props.reportGeneration,
      'serial-number': serialNumber,
      'install-date': installationDate.format('YYYY/MM/DD'),
      'co2-emission-factor': co2EmissionFactor,
      'report-issue': props.periodicReportInfo['report-issue'],
      'currency': props.periodicReportInfo['currency'],
      'priceA': props.periodicReportInfo['priceA'],
      'priceA-rate': props.periodicReportInfo['priceA-rate'],
      'priceB': props.periodicReportInfo['priceB'],
      'priceB-rate': props.periodicReportInfo['priceB-rate'],
      'priceC': props.periodicReportInfo['priceC'],
      'priceC-rate': props.periodicReportInfo['priceC-rate'],
      'priceD': props.periodicReportInfo['priceD'],
      'priceD-rate': props.periodicReportInfo['priceD-rate'],
      'priceE': props.periodicReportInfo['priceE'],
      'priceE-rate': props.periodicReportInfo['priceE-rate'],
      'priceF': props.periodicReportInfo['priceF'],
      'priceF-rate': props.periodicReportInfo['priceF-rate'],
      'price-schedule': props.periodicReportInfo['price-schedule'],
      'alternative-fuel1': props.periodicReportInfo['alternative-fuel1'],
      'alternative-fuel1-rate':
        props.periodicReportInfo['alternative-fuel1-rate'],
      'alternative-fuel1-unit':
        props.periodicReportInfo['alternative-fuel1-unit'],
      'alternative-fuel1-efficiency':
        props.periodicReportInfo['alternative-fuel1-efficiency'],
      'alternative-fuel1-co2-emission-factor':
        props.periodicReportInfo['alternative-fuel1-co2-emission-factor'],
      'alternative-fuel2': props.periodicReportInfo['alternative-fuel2'],
      'alternative-fuel2-rate':
        props.periodicReportInfo['alternative-fuel2-rate'],
      'alternative-fuel2-unit':
        props.periodicReportInfo['alternative-fuel2-unit'],
      'alternative-fuel2-efficiency':
        props.periodicReportInfo['alternative-fuel2-efficiency'],
      'alternative-fuel2-co2-emission-factor':
        props.periodicReportInfo['alternative-fuel2-co2-emission-factor'],
      'refrigerant': refrigerant,
    };

    const patchData: apiIF.reqParameters.patchCompositionsRTUnit = {
      'periodic-report': periodicReportSetting,
    };

    patchCompositionsRTUnit(props.siteID, props.gwID, props.unitID, patchData)
      .then(() => {
        setIsOpenSuccessDialog(true);
        props.setCompositionsInSite(prev => {
          return prev.map(gwInfo => {
            return {
              ...gwInfo,
              'unit-informations': gwInfo['unit-informations'].map(unitInfo => {
                if (unitInfo['unit-id'] === props.unitID) {
                  return {
                    ...unitInfo,
                    'periodic-report': periodicReportSetting,
                  };
                }
                return unitInfo;
              }),
            };
          });
        });
      })
      .catch(err => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  if (props.reportGeneration === 'inactive') {
    return (
      <>
        {/** ボタン */}
        <Grid
          container
          justifyContent="flex-end"
          spacing={3}
          className="serviceInfo__periodicReportSetting__contents__button">
          <Grid item xs="auto">
            <SecondaryButton
              disabled={apiWait}
              onClick={handleBack}
              buttonText={props.trans('PeriodicReportSetting.buttonTxt.back')}
            />
          </Grid>
          <Grid item xs="auto">
            <PrimaryButton
              onClick={handleApply}
              disabled={applyBtnDisabled() || apiWait}
              buttonText={props.trans('PeriodicReportSetting.buttonTxt.apply')}
              apiWait={apiWait}
            />
          </Grid>
        </Grid>
        <SuccessMessageDialog
          open={isOpenSuccessDialog}
          setOpen={setIsOpenSuccessDialog}
          trans={props.trans}
        />
        <FailMessageDialog
          open={isOpenErrorDialog}
          setOpen={setIsOpenErrorDialog}
          trans={props.trans}
        />
      </>
    );
  }
  return (
    <>
      {/** 一般設定 */}
      <Grid item xs={12} container>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__subTitle">
          <h3>{props.trans('PeriodicReportSetting.heading.commonInfo')}</h3>
        </Grid>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__unitInfo">
          <Stack
            spacing={5}
            className="serviceInfo__periodicReportSetting__contents__unitInfo__space">
            {/** ユニット型式 */}
            <TextField
              id="nameplateChiller"
              className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
              label={props.trans('PeriodicReportSetting.unitModel')}
              value={props.nameplate}
              InputProps={{
                readOnly: true,
              }}
              variant="standard"
            />
            {/** 電源電圧 */}
            <Grid container alignItems="center">
              <Grid item xs="auto">
                <TextField
                  id="periodicReportSetting-voltage"
                  className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
                  label={props.trans('PeriodicReportSetting.voltage')}
                  value={
                    props.inputVoltage === '' ||
                    props.inputVoltage === undefined
                      ? props.trans('PeriodicReportSetting.noSetting')
                      : props.inputVoltage
                  }
                  InputProps={
                    props.inputVoltage === '' ||
                    props.inputVoltage === undefined
                      ? {readOnly: true}
                      : {
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">V</InputAdornment>
                          ),
                        }
                  }
                  variant="standard"
                />
              </Grid>
            </Grid>
            {/** 製造番号入力欄 */}
            <ChkInputTextbox
              setValue={setSerialNumber}
              setError={setSerialNumberError}
              chkInputTxtOK={format.PERIODIC_REPORT_SETTING_SERIAL_NUMBER_OK}
              type="text"
              label={props.trans('PeriodicReportSetting.serialNumber')}
              value={serialNumber}
              id="mv-inputApplySerialNumber"
              class="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
              helperText=" "
              errHelperText={props.trans(
                'PeriodicReportSetting.txtField.errIncorrectEntry'
              )}
            />
            {/** 据付日入力欄 */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="serviceInfo__periodicReportSetting__contents__unitInfo__chkInputTextBox"
                //prettier-ignore
                label={`${props.trans('PeriodicReportSetting.installationDate')}*`}
                onChange={newValue => setInstallationDate(newValue)}
                slotProps={{
                  textField: {
                    error:
                      installationDate === null ||
                      installationDate.isValid() === false,
                    helperText:
                      installationDate === null ||
                      installationDate.isValid() === false
                        ? props.trans(
                            'PeriodicReportSetting.txtField.errIncorrectEntry'
                          )
                        : ' ',
                  },
                }}
                value={installationDate}
                views={['year', 'month', 'day']}
              />
            </LocalizationProvider>

            {/** 冷媒 */}
            <TextFieldSelectBox
              choices={pullDownMenu.UNIT_REFRIGERANT}
              setValue={setRefrigerant}
              label={`${props.trans('PeriodicReportSetting.refrigerant')}*`}
              value={refrigerant}
              id="selectRefrigerant"
              class="serviceInfo__periodicReportSetting__contents__unitInfo__textFieldSelectBox"
            />
          </Stack>
        </Grid>
      </Grid>
      {/** 電力CO2排出係数 */}
      <Grid item xs={12} container>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__subTitle">
          <h3>
            {props.trans('PeriodicReportSetting.heading.co2EmissionFactor')}
          </h3>
        </Grid>
        <Grid
          item
          xs={12}
          className="serviceInfo__periodicReportSetting__contents__electricBillInfo">
          <Stack>
            {/** CO2排出係数入力欄 */}
            <Grid
              container
              alignItems="center"
              className="serviceInfo__periodicReportSetting__contents__co2EmissionFactor">
              <Grid item xs="auto">
                <ChkInputTextbox
                  setValue={setCo2EmissionFactor}
                  setError={setCo2EmissionFactorError}
                  chkInputTxtOK={
                    format.PERIODIC_REPORT_SETTING_CO2_EMISSION_PATTERN_OK
                  }
                  type="text"
                  label={`${props.trans(
                    'PeriodicReportSetting.electricCO2EmissionFactor'
                  )}*`}
                  value={co2EmissionFactor}
                  id="mv-inputApplyCo2EmissionFactor"
                  class="serviceInfo__periodicReportSetting__contents__electricBillInfo__chkInputTextBox--co2EmissionFactor"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {props.trans(
                          'PeriodicReportSetting.unit.electricCO2EmissionFactor'
                        )}
                      </InputAdornment>
                    ),
                  }}
                  helperText=" "
                  errHelperText={props.trans(
                    'PeriodicReportSetting.txtField.errIncorrectEntry'
                  )}
                />
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
      {/** ボタン */}
      <Grid
        container
        justifyContent="flex-end"
        spacing={3}
        className="serviceInfo__periodicReportSetting__contents__button">
        <Grid item xs="auto">
          <SecondaryButton
            disabled={apiWait}
            onClick={handleBack}
            buttonText={props.trans('PeriodicReportSetting.buttonTxt.back')}
          />
        </Grid>
        <Grid item xs="auto">
          <PrimaryButton
            onClick={handleApply}
            disabled={applyBtnDisabled() || apiWait}
            buttonText={props.trans('PeriodicReportSetting.buttonTxt.apply')}
            apiWait={apiWait}
          />
        </Grid>
      </Grid>
      <SuccessMessageDialog
        open={isOpenSuccessDialog}
        setOpen={setIsOpenSuccessDialog}
        trans={props.trans}
      />
      <FailMessageDialog
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        trans={props.trans}
      />
    </>
  );
};

const SuccessMessageDialog = (props: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id?: string;
  trans: TFunction;
}) => {
  const handleClose = () => {
    props.setOpen(false);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        {props.trans('PeriodicReportSetting.successDlgMsg.title')}
      </DialogTitle>
      <DialogContent>
        {/* ダイアログに表示するメッセージ */}
        <DialogContentText id="mv-processError-text">
          {props.trans('PeriodicReportSetting.successDlgMsg.msg')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          onClick={handleClose}
          buttonText={props.trans('PeriodicReportSetting.buttonTxt.ok')}
          id={props.id}
        />
      </DialogActions>
    </Dialog>
  );
};

const FailMessageDialog = (props: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id?: string;
  trans: TFunction;
}) => {
  const handleClose = () => {
    props.setOpen(false);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title" style={{color: 'red'}}>
        {props.trans('PeriodicReportSetting.failDlgMsg.title')}
      </DialogTitle>
      <DialogContent>
        {/* ダイアログに表示するメッセージ */}
        <DialogContentText id="mv-processError-text" style={{color: 'red'}}>
          {props.trans('PeriodicReportSetting.failDlgMsg.msg')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          onClick={handleClose}
          buttonText={props.trans('PeriodicReportSetting.buttonTxt.ok')}
          id={props.id}
        />
      </DialogActions>
    </Dialog>
  );
};

export default PeriodicReportSetting;
